import { AgentAPI, autoCancelMap, cancelRequest } from '@apis'
import axios, { AxiosInstance } from 'axios'
import { alerts } from '@/ui/useAlertStore'
import { toast } from '@/ui/useToastStore'

/** 選項 */
type Options<T = any> = {
  rejected?: (error: T) => T
}

/**
 * 攔截器
 * @example
 * defineMiddleware(SportAPI)
 * defineMiddleware(ChatAPI)
 * defineMiddleware(CmsAPI)
 */
const defineMiddleware = (instance: AxiosInstance, options?: Options) => {
  if (((instance.interceptors.response as any)?.handlers?.length || -1) > 0) return

  /** Request Middleware */
  instance.interceptors.request.use(async function (config) {
    // auto cancel
    if (!config?.signal && (config.autoCancel === undefined || config.autoCancel)) {
      const key = typeof config.autoCancel === 'string' ? config.autoCancel : config.url + (config.data || config.params ? JSON.stringify(config.params) : '')
      // 自動取消上一次
      cancelRequest(key)
      const controller = new AbortController()
      autoCancelMap[key] = controller
      config.cancelControllerKey = key
      config.signal = controller.signal
    }

    return config
  })

  /** Response Middleware */
  instance.interceptors.response.use(
    function (res) {
      // 已請求結束，刪除自動取消
      if (!res?.config?.signal && res?.config?.cancelControllerKey) delete autoCancelMap[res.config.cancelControllerKey]
      // 解構
      return res.data
    },
    function (error) {
      // 取消不處理任何事
      if (axios.isCancel(error)) return Promise.reject(error)
      // 無視錯誤處理: 通常用於避免自動登出或影響邏輯的判斷
      if (error?.config?.ignoreError) return Promise.resolve(error)
      // 已請求結束，刪除自動取消
      if (!error?.config?.signal && error?.config?.cancelControllerKey) delete autoCancelMap[error.config.cancelControllerKey]
      // 錯誤訊息
      const errorMessage = error?.response?.data?.msg || String(error)
      /** 自動顯示 Toast */
      if (error.config?.toast) {
        if (error?.config?.toast === true) toast(errorMessage)
        else if (typeof error?.config?.toast === 'function') toast(error?.config?.toast(error))
        else toast(error.config.toast || errorMessage)
      }
      /** 自動顯示 Alert */
      if (error.config?.alerts) {
        if (error?.config?.alerts === true) alerts({ message: errorMessage, close: false })
        else if (typeof error?.config?.alerts === 'function') alerts(error?.config?.alerts(error))
        else if (typeof error?.config.alerts === 'object' && error?.config.alerts !== null) alerts({ message: errorMessage, close: false, ...error.config.alerts })
      }
      // 各商戶客製化錯誤處理
      if (options?.rejected) return options.rejected(error)
      // 預設
      else return Promise.reject(error)
    },
  )
}

/** 商戶客製化處理 */
export const bindMiddleware = (options?: Options) => {
  defineMiddleware(AgentAPI, options)
}
